import * as XLSX from 'xlsx';

/**
 * Build an XLSX workbook from a plain row array and trigger a download.
 *
 * Each row is a `Record<string, unknown>`; keys become column headers (in
 * the order they appear on the first non-empty row). Pass already-formatted
 * values (Date.toISOString() for dates, `Number` for numerics) — SheetJS
 * preserves the JS type when it can.
 *
 * Example:
 *   downloadXlsx('xotskies.xlsx', 'Xotskies', rows);
 *
 * Implementation notes:
 *  - Bundle cost: SheetJS adds ~370 KB minified. Worth it for first-class
 *    Excel files (dates, numbers, formulas). If we ever need to slim down,
 *    CSV would cover 90% of the use-cases.
 *  - The filename is sanitised — `/` and other path separators get replaced
 *    so user-supplied names can't escape the browser's download directory.
 */
export function downloadXlsx(
  filename: string,
  sheetName: string,
  rows: Record<string, unknown>[],
): void {
  const ws = XLSX.utils.json_to_sheet(rows, { cellDates: true });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, truncateSheetName(sheetName));
  XLSX.writeFile(wb, sanitiseFilename(filename));
}

function sanitiseFilename(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'export';
  return cleaned.toLowerCase().endsWith('.xlsx') ? cleaned : `${cleaned}.xlsx`;
}

/** Excel limits sheet names to 31 chars and forbids /\?*[]: */
function truncateSheetName(name: string): string {
  return name.replace(/[\\/?*[\]:]/g, '_').slice(0, 31) || 'Sheet1';
}
